/**
 * Telemetry for callable rejections the UI turns into a friendly message.
 *
 * THE DEFECT THIS CLOSES (ISS-460). Every schedule-block Update failed with a
 * server `invalid-argument` for four days and nothing reported it. Two things
 * combined to make that invisible:
 *   1. the hook showed the server's message as a toast — correct for the user,
 *      but a handled error emits nothing; and
 *   2. the network interceptor (consoleAndNetworkCapture.handleNetworkError)
 *      deliberately reports only status 0 and 5xx, so a 4xx rejection never
 *      reaches Sentry by that route either.
 * A callable failing 100% of the time therefore looked identical to one nobody
 * used.
 *
 * WHAT IT SENDS. One `warning` event per rejection, fingerprinted
 * `["callable-rejection", callableName, code]` so each callable+code is ONE
 * Sentry issue whose event count rises with the failure rate. This helper adds
 * only the callable name, the code and a de-identified route (query, hash and
 * id / email / long-slug segments removed). It NEVER adds the server's message
 * text or the request payload: both routinely contain customer names,
 * addresses and free-text notes.
 *
 * WHAT THE SDK STILL ATTACHES, stated so nobody over-trusts the line above:
 *   - `request.url` — the browser integration stamps the full page URL (query
 *     and hash included) on every event. A scope-local event processor below
 *     REPLACES it with origin + the de-identified route, for these events only
 *     (scrubUrl.ts `addScrubbedRequestProcessor`).
 *   - `request.headers.Referer` — also stamped by the browser integration from
 *     `document.referrer`, which can carry ids and query strings. The same
 *     processor DROPS it (any casing), along with `query_string` and cookies.
 *   - breadcrumbs — the default navigation/fetch/console breadcrumbs, exactly as
 *     on every other event this app sends. NOT scrubbed here; that is app-wide
 *     Sentry config, not this helper's to change.
 *   - no session replay is triggered: replay's on-error sampling requires an
 *     `exception`, and a captureMessage event has none.
 *
 * WHAT IT DOES NOT SEND. Codes the network interceptor already reports (the
 * 5xx / status-0 family: `internal`, `unavailable`, `unknown`,
 * `deadline-exceeded`, `data-loss`, `unimplemented`) are excluded so a single
 * failure is never double-counted, and `unauthenticated` is excluded because a
 * signed-out tab is expected, not a defect. Non-callable errors (a Firestore
 * `failed-precondition` has no `functions/` prefix) are ignored.
 *
 * Imports only `@sentry/react` (plus ./scrubUrl, which imports nothing), which every hosting app (client, client-tech,
 * sales-client, time-client) lists in its own package.json — see
 * buildIdentity.ts for why a shared file must not add a bare specifier that a
 * consumer lacks.
 */
import * as Sentry from '@sentry/react';
import { addScrubbedRequestProcessor, currentRoute } from './scrubUrl';

/** Server-declined (4xx-class) callable codes worth a signal. */
const REPORTABLE_CODES: ReadonlySet<string> = new Set([
  'invalid-argument',
  'failed-precondition',
  'out-of-range',
  'not-found',
  'already-exists',
  'aborted',
  'permission-denied',
  'resource-exhausted',
]);

/**
 * The callables whose call sites already call `reportCallableRejection`. reportCallableHttpRejection (the interceptor path) skips
 * these so a rejection is never counted twice. reportCallableHttpRejection.coverage.test.ts fails when this list and the source disagree.
 */
export const REPORTED_BY_CALL_SITE: ReadonlySet<string> = new Set([
  'createScheduleBlock',
  'deleteScheduleBlock',
  'forms_createSubmission',
  'timeTracking_exportToQBO',
  'timeTracking_revertExport',
  'updateScheduleBlock',
  'updateServiceFile',
]);

const FUNCTIONS_PREFIX = 'functions/';

/** The bare callable code (`invalid-argument`) for a FunctionsError, else null. */
export function callableRejectionCode(err: unknown): string | null {
  try {
    const code = (err as { code?: unknown } | null)?.code;
    if (typeof code !== 'string' || !code.startsWith(FUNCTIONS_PREFIX)) return null;
    const bare = code.slice(FUNCTIONS_PREFIX.length);
    return REPORTABLE_CODES.has(bare) ? bare : null;
  } catch {
    return null;
  }
}

/**
 * Caller-supplied detail, narrowed to string tags.
 *
 * OFFICEHUB-CLIENT-61 is what this exists for: an
 * `updateScheduleBlock (invalid-argument)` event with no payload, against a
 * server with SEVEN distinct invalid-argument branches. The call site is the
 * only place that knows which facts about its own payload are non-sensitive —
 * see payloadShape.ts, which describes a payload by field NAME and never by
 * value.
 *
 * Non-string values are DROPPED rather than stringified: an object would be
 * serialised into a tag, and a nested object is exactly where a value (a
 * customer name, an address) would hide.
 */
function safeDetailTags(detail?: Readonly<Record<string, string>>): Record<string, string> {
  if (!detail) return {};
  const safe: Record<string, string> = {};
  for (const [key, value] of Object.entries(detail)) {
    if (typeof value === 'string') safe[key] = value;
  }
  return safe;
}

/**
 * Report a server rejection of `callableName` that the UI is handling as a
 * friendly message. Call it from the same branch that shows the message.
 * Never throws — telemetry must not be able to break the error path it observes.
 *
 * `detail` is optional, non-sensitive, string-valued context (payload SHAPE,
 * not payload). It cannot override `callable`, `code` or `route`: a call site
 * must not be able to mislabel which callable failed.
 */
export function reportCallableRejection(
  callableName: string,
  err: unknown,
  detail?: Readonly<Record<string, string>>,
): void {
  const code = callableRejectionCode(err);
  if (!code) return;
  try {
    const route = currentRoute();
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.setFingerprint(['callable-rejection', callableName, code]);
      scope.setTags({ ...safeDetailTags(detail), callable: callableName, code, route });
      addScrubbedRequestProcessor(scope);
      Sentry.captureMessage(`Callable rejected: ${callableName} (${code})`);
    });
  } catch {
    /* a reporting failure must never surface to the user */
  }
}
